import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { formatApiError } from "@/lib/format-api-error";
import { queryKeys } from "@/lib/query-keys";

// POST /api/admin/listing-changes/:id/decline — body { note?: string }
//
// Unlike MDF (reason required), the note is optional; when present the
// portal shows it verbatim to the partner. Pessimistic, mirroring
// use-approve-listing-change; 409 already_processed means a parallel
// reviewer got there first, so refresh instead of erroring loudly.
type Vars = { id: string; note?: string };

export function useDeclineListingChange() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, Vars>({
    mutationFn: ({ id, note }) =>
      apiFetch<{ ok: true }>(`/admin/listing-changes/${id}/decline`, {
        method: "POST",
        body: JSON.stringify(note ? { note } : {}),
      }),
    onSuccess: () => {
      toast.success("Change declined");
      void queryClient.invalidateQueries({
        queryKey: queryKeys.admin.listingChanges.all,
      });
    },
    onError: (err) => {
      if (err.status === 409) {
        toast.error("Already processed — refreshing");
        void queryClient.invalidateQueries({
          queryKey: queryKeys.admin.listingChanges.all,
        });
        return;
      }
      toast.error(formatApiError(err, "Failed to decline change"));
    },
  });
}
